import { useMemo, useState } from "react";
import { Card, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCertifySpvPoolGate,
  useGetPoolStageGates,
} from "@/hooks/capitalMarkets/useSpvPools";
import {
  SPV_POOL_STATUSES,
  type RequiredCertificationDto,
  type SpvPoolDto,
  type SpvPoolStatus,
  type StageGateGroup,
} from "@/types/spvPool";
import { humanizeStatus } from "../../shared/format";
import { AdvanceStatusDialog, GateStatusChip } from "../components/AdvanceStatusDialog";

interface CertificationsTabProps {
  pool: SpvPoolDto;
}

/**
 * SPV Pool v1.0 PR 8 §6.2 — the pool's Certifications tab. Stage-gate
 * definitions are grouped by transition; the gates for the pool's next
 * transition are highlighted, and an "Advance to …" affordance stays
 * disabled until every next-stage gate is approved.
 */
export function CertificationsTab({ pool }: CertificationsTabProps) {
  const gatesQuery = useGetPoolStageGates(pool.id);
  const [advanceOpen, setAdvanceOpen] = useState(false);

  const groups = gatesQuery.data ?? [];

  const nextStatus = useMemo<SpvPoolStatus | undefined>(() => {
    const idx = SPV_POOL_STATUSES.indexOf(pool.status);
    return idx >= 0 ? SPV_POOL_STATUSES[idx + 1] : undefined;
  }, [pool.status]);

  const nextGroup = groups.find((g) => g.fromStatus === pool.status);
  const missingNextGates = (nextGroup?.gates ?? []).filter(
    (g) => g.currentStatus !== "Approved",
  );
  const canAdvance =
    !!nextGroup && nextStatus !== undefined && missingNextGates.length === 0;

  return (
    <div className="flex flex-col gap-space-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-space-3">
          <div>
            <h3 className="font-display font-bold text-h3 m-0">
              Stage-Gate Certifications
            </h3>
            <p className="text-small text-gray-mid mt-space-1 mb-0">
              Current stage:{" "}
              <span className="font-bold text-onyx">
                {humanizeStatus(pool.status)}
              </span>
            </p>
          </div>
          {nextStatus && (
            <span
              title={
                canAdvance
                  ? undefined
                  : `Blocked — certify: ${
                      missingNextGates.map((g) => g.name).join(", ") ||
                      "no gates loaded"
                    }`
              }
            >
              <Button
                variant="primary"
                size="sm"
                disabled={!canAdvance}
                onClick={() => setAdvanceOpen(true)}
                data-testid="advance-to-next"
              >
                Advance to {humanizeStatus(nextStatus)}
              </Button>
            </span>
          )}
        </div>
      </Card>

      {gatesQuery.isLoading && (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading stage gates…
          </p>
        </Card>
      )}

      {!gatesQuery.isLoading && groups.length === 0 && (
        <Card>
          <p className="text-small text-gray-mid m-0">
            No stage-gate certifications have been seeded for SPV pools.
          </p>
        </Card>
      )}

      {groups.map((group) => (
        <GateGroupSection
          key={`${group.fromStatus}→${group.toStatus}`}
          group={group}
          poolId={pool.id}
          isNext={group.fromStatus === pool.status}
        />
      ))}

      {nextStatus && (
        <AdvanceStatusDialog
          open={advanceOpen}
          onClose={() => setAdvanceOpen(false)}
          pool={pool}
          targetStatus={nextStatus}
        />
      )}
    </div>
  );
}

function GateGroupSection({
  group,
  poolId,
  isNext,
}: {
  group: StageGateGroup;
  poolId: string;
  isNext: boolean;
}) {
  const count = group.gates.length;
  return (
    <Card
      className={
        isNext ? "border-2 border-gold" : undefined
      }
      data-testid={isNext ? "next-stage-group" : "stage-group"}
    >
      <details open={isNext}>
        <summary className="cursor-pointer select-none font-display font-bold text-onyx">
          {humanizeStatus(group.fromStatus)} →{" "}
          {humanizeStatus(group.toStatus)}{" "}
          <span className="text-gray-mid font-normal">
            ({count} {count === 1 ? "gate" : "gates"})
          </span>
          {isNext && (
            <span className="ml-space-2 text-gold-shadow text-small">
              Next transition
            </span>
          )}
        </summary>
        <ul className="flex flex-col gap-space-2 m-0 mt-space-3 p-0 list-none">
          {group.gates.map((gate) => (
            <GateRow
              key={gate.certificationDefinitionId}
              gate={gate}
              poolId={poolId}
            />
          ))}
        </ul>
      </details>
    </Card>
  );
}

function GateRow({
  gate,
  poolId,
}: {
  gate: RequiredCertificationDto;
  poolId: string;
}) {
  const toast = useToast();
  const certify = useCertifySpvPoolGate(poolId);
  const needsAttention = gate.currentStatus !== "Approved";

  const handleCertify = () => {
    certify
      .mutateAsync({
        definitionId: gate.certificationDefinitionId,
        note: `Stage-gate certified from the ${gate.stageFrom} → ${gate.stageTo} checklist.`,
      })
      .then(() => toast.success(`"${gate.name}" certified.`))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <li
      className="flex items-start gap-space-3 rounded-md border border-border-light p-space-3"
      data-testid="gate-row"
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-small font-bold text-onyx">{gate.name}</span>
          <GateStatusChip status={gate.currentStatus} />
        </div>
        <p className="text-small text-gray-mid mt-space-1 mb-0">
          {gate.description}
        </p>
        <div className="flex flex-wrap items-center gap-space-1 mt-space-2">
          <span className="text-label uppercase tracking-label text-gray-mid">
            Roles:
          </span>
          {gate.requiredRoles.map((role) => (
            <span
              key={role}
              className="px-space-2 py-[2px] rounded-sm bg-white-warm text-onyx text-[10px] font-bold"
            >
              {role}
            </span>
          ))}
        </div>
      </div>
      {needsAttention && (
        <Button
          variant="secondary"
          size="sm"
          loading={certify.isPending}
          onClick={handleCertify}
          data-testid="gate-certify"
        >
          {gate.currentStatus === "Revoked" ? "Re-certify" : "Certify"}
        </Button>
      )}
    </li>
  );
}
